<template>
  <generic-modal
    :title="$t('customer details')"
    ref="generic_modal"
    :is_wide="true"
  >
    <!-- Customer Information -->
    <div class="columns" v-if="meta.name">
      <div class="column">
        <div class="box">
          <h2 class="title is-5">{{ $t("customer information") }}</h2>

          <div class="field">
            <label class="label">{{ $t("customer name") }}</label>
            <p class="has-text-weight-semibold">{{ meta.name }}</p>
          </div>

          <div class="field">
            <label class="label">{{ $t("identification number") }}</label>
            <p class="has-text-weight-semibold">
              {{ meta.identification_number }}
            </p>
          </div>

          <div class="field">
            <label class="label">{{ $t("created date") }}</label>
            <p>{{ format_date(meta.create_date) }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Related Expedients -->
    <div class="columns" v-if="expedients.length > 0">
      <div class="column">
        <div class="box">
          <h2 class="title is-5">
            {{ $t("expedients") }} ({{ expedients.length }})
          </h2>

          <div class="table-container">
            <table class="table is-fullwidth is-striped">
              <thead>
                <tr>
                  <th>{{ $t("expedient title") }}</th>
                  <th>{{ $t("account number") }}</th>
                  <th>{{ $t("status") }}</th>
                  <th>{{ $t("files") }}</th>
                  <th>{{ $t("actions") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="expedient in expedients"
                  :key="expedient.expedient_id"
                >
                  <td>{{ expedient.title }}</td>
                  <td>{{ expedient.account_number }}</td>
                  <td>
                    <span
                      class="tag"
                      :class="{
                        'is-success': expedient.status === 'active',
                        'is-warning': expedient.status === 'closed',
                        'is-info': expedient.status === 'archived',
                      }"
                    >
                      {{ expedient.status }}
                    </span>
                  </td>
                  <td>{{ expedient.file_count || 0 }}</td>
                  <td>
                    <button
                      class="button is-small is-info"
                      @click="view_expedient(expedient.uuid)"
                    >
                      <span class="icon"><i class="fa fa-eye"></i></span>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- No expedients message -->
    <div class="columns" v-else-if="!loading && meta.name">
      <div class="column">
        <div class="box has-text-centered">
          <p class="has-text-grey">{{ $t("no expedients found") }}</p>
        </div>
      </div>
    </div>

    <!-- Error handling -->
    <div class="notification is-danger mt-5" v-if="error">
      <p v-html="error"></p>
    </div>

    <!-- Footer buttons -->
    <template v-slot:footer>
      <button
        class="button is-primary is-rounded"
        @click="edit_customer"
        v-if="meta.uuid"
      >
        <span class="icon"><i class="fa fa-edit"></i></span>
        <span>{{ $t("edit") }}</span>
      </button>
      <button class="button is-rounded" @click="close_modal">
        <span class="icon"><i class="fa fa-close"></i></span>
        <span>{{ $t("close") }}</span>
      </button>
    </template>
  </generic-modal>
</template>

<script>
import { get, generate_error_message_text, format_date } from "@/utils";
import GenericModal from "@/components/generic_modal/Index.vue";

export default {
  name: "CustomerDetailModal",
  components: { GenericModal },
  data() {
    return {
      meta: {},
      expedients: [],
      error: "",
      loading: false,
    };
  },
  methods: {
    close_modal() {
      this.$refs.generic_modal.close_modal();
      this.set_initial_state();
    },

    show_modal(uuid = null) {
      this.set_initial_state();

      if (uuid) {
        this.loading = true;

        // Load customer details
        get("/archive/customer", { uuid })
          .then((customer) => {
            this.meta = customer;
            this.$refs.generic_modal.show_modal();

            // Load related expedients
            return this.load_expedients(customer.customer_id);
          })
          .catch((error) => {
            this.error = generate_error_message_text(error);
          })
          .finally(() => {
            this.loading = false;
          });
      }
    },

    load_expedients(customer_id) {
      // In a real implementation, you would have an endpoint to get expedients by customer
      // For now, we'll simulate it
      return new Promise((resolve) => {
        // Simulated expedients data
        this.expedients = [];
        resolve();
      });
    },

    edit_customer() {
      this.$emit("edit", this.meta.uuid);
      this.close_modal();
    },

    view_expedient(expedient_uuid) {
      // Navigate to expedient details or emit event
      this.$router.push({
        path: "/archive/expedients",
        query: { uuid: expedient_uuid },
      });
    },

    format_date(date) {
      return format_date(date);
    },

    set_initial_state() {
      this.meta = {};
      this.expedients = [];
      this.error = "";
      this.loading = false;
    },
  },
};
</script>

<style scoped>
.box {
  margin-bottom: 1rem;
}

.title.is-5 {
  margin-bottom: 1rem;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 0.5rem;
}

.field {
  margin-bottom: 1rem;
}

.label {
  margin-bottom: 0.25rem;
}

.table-container {
  max-height: 300px;
  overflow-y: auto;
}
</style>
